/**
 * Recent Activity Widget
 * Displays recent system activity and alerts
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { ALERT_TYPES } from '../types/widgetTypes';

const RecentActivity = ({ 
  activities = [], 
  stats = null,
  loading = false,
  showTitle = true,
  customTitle = "Recent Activity",
  maxItems = 5,
  className = ""
}) => {
  // Generate activity items from stats if no activities provided
  const generateActivitiesFromStats = (stats) => {
    const activities = [];
    
    if (stats?.reservations?.active > 0) {
      activities.push({
        id: 'active-reservations',
        type: 'info',
        icon: ClockIcon,
        title: `${stats.reservations.active} active reservations`,
        description: `${stats.reservations.active} equipment items currently in use`,
        action: {
          label: 'View All',
          href: '/reservations?status=active'
        },
        timestamp: new Date()
      });
    } else {
      activities.push({
        id: 'no-active-reservations',
        type: 'success',
        icon: CheckCircleIcon,
        title: 'No active reservations',
        description: 'All equipment is currently available',
        timestamp: new Date()
      });
    }

    if (stats?.inventory?.maintenanceDue > 0) {
      activities.push({
        id: 'maintenance-due',
        type: 'warning',
        icon: ExclamationTriangleIcon,
        title: `${stats.inventory.maintenanceDue} items need maintenance`,
        description: 'Some inventory items require servicing',
        action: {
          label: 'Manage Inventory',
          href: '/inventory'
        },
        timestamp: new Date()
      });
    }

    if (stats?.inventory?.statusCounts?.OUT_OF_SERVICE > 0) {
      activities.push({
        id: 'out-of-service',
        type: 'error',
        icon: XCircleIcon,
        title: `${stats.inventory.statusCounts.OUT_OF_SERVICE} items out of service`,
        description: 'Equipment currently unavailable',
        action: {
          label: 'View Details',
          href: '/inventory?status=out-of-service'
        },
        timestamp: new Date()
      });
    }

    return activities;
  };

  const displayActivities = activities.length > 0 
    ? activities 
    : stats 
      ? generateActivitiesFromStats(stats) 
      : [];

  const limitedActivities = displayActivities.slice(0, maxItems);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getActivityStyles = (type) => {
    return ALERT_TYPES[type.toUpperCase()] || ALERT_TYPES.INFO;
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        {showTitle && (
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
          </div>
        )}
        <div className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mt-2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{customTitle}</h2>
        </div>
      )}
      <div className="p-6">
        {limitedActivities.length === 0 ? (
          <div className="text-center py-8">
            <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No recent activity</p>
            <p className="text-sm text-gray-400 mt-1">Activity will appear here as it happens</p>
          </div>
        ) : (
          <div className="space-y-4">
            {limitedActivities.map((activity) => {
              const IconComponent = activity.icon || InformationCircleIcon;
              const styles = getActivityStyles(activity.type);
              
              return (
                <div 
                  key={activity.id} 
                  className={`flex items-center justify-between p-4 ${styles.bgColor} ${styles.borderColor} border rounded-lg`}
                >
                  <div className="flex items-center space-x-4">
                    <IconComponent className={`h-5 w-5 ${styles.accentColor} flex-shrink-0`} />
                    <div className="flex-1">
                      <p className={`font-medium ${styles.textColor}`}>
                        {activity.title}
                      </p>
                      <p className={`text-sm ${styles.textColor} opacity-75`}>
                        {activity.description}
                      </p>
                      {activity.timestamp && (
                        <p className={`text-xs ${styles.textColor} opacity-60 mt-1`}>
                          {formatTimeAgo(activity.timestamp)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {activity.action && (
                    <Link
                      to={activity.action.href}
                      className={`text-sm font-medium ${styles.accentColor} hover:opacity-80`}
                    >
                      {activity.action.label} →
                    </Link>
                  )}
                </div>
              );
            })}
            
            {displayActivities.length > maxItems && (
              <div className="text-center pt-4">
                <button className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                  View All Activity ({displayActivities.length - maxItems} more)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;